import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DateRangePicker.css";

/**
 * Date-range picker for the coaching meetings views.
 *
 * Differences from the shared AppLogs RangeDatePicker (which is intentionally
 * left untouched because AppLogs relies on its 14-day cap):
 *  - No range-length limit.
 *  - Starts empty (placeholder) instead of defaulting to today–today, so it is
 *    obvious when no date filter is applied.
 *  - Has a "Temizle" (clear) action that resets to "all dates" — emits
 *    { minDate: null, maxDate: null } so the parent can drop the date filter.
 *
 * Contract: onChangeDateRange({ minDate, maxDate }) receives Date objects when a
 * full range is picked, and nulls when cleared.
 */
export default function DateRangePicker({ onChangeDateRange, placeholder }) {
  const [range, setRange] = useState([null, null]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  const [start, end] = range;

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function emit(min, max) {
    if (onChangeDateRange) onChangeDateRange({ minDate: min, maxDate: max });
  }

  function handleChange(dates) {
    const [nextStart, nextEnd] = dates;
    setRange(dates);
    // Only apply once a full range is chosen; then close.
    if (nextStart && nextEnd) {
      emit(nextStart, nextEnd);
      setOpen(false);
    }
  }

  function handleClear(e) {
    if (e) e.stopPropagation();
    setRange([null, null]);
    emit(null, null);
    setOpen(false);
  }

  const displayValue = start
    ? `${start.toLocaleDateString("en-GB")}${
        end ? ` - ${end.toLocaleDateString("en-GB")}` : ""
      }`
    : "";

  return (
    <div className="date-range-picker" ref={wrapperRef}>
      <div className="date-range-input-wrapper">
        <input
          type="text"
          readOnly
          className="date-range-input"
          placeholder={placeholder || "Tüm Tarihler"}
          value={displayValue}
          onClick={() => setOpen((o) => !o)}
        />
        {start && (
          <button
            type="button"
            className="date-range-clear-x"
            onClick={handleClear}
            aria-label="Tarihi temizle"
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <div className="date-range-popover">
          <DatePicker
            selected={start}
            onChange={handleChange}
            startDate={start}
            endDate={end}
            selectsRange
            inline
            dateFormat="dd/MM/yyyy"
          />
          <div className="date-range-actions">
            <button
              type="button"
              className="date-range-clear-btn"
              onClick={handleClear}
            >
              Temizle
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
